import { useState } from 'react';

import { useFetchIsBudgetOverageEnabled } from '@webapp/api/procurify/hooks/BudgetOverage/useFetchIsBudgetOverageEnabled'
import { toggleIsBudgetOverageEnabled } from '@webapp/api/SettingsAPI'
import { toast } from '@webapp/components/Notification'
import { useProcurifyIntl } from '@webapp/hooks'
import { parseAPIError } from '@webapp/utils/errors'
import { BudgetCategoryBudgetSettingsModal } from './BudgetCategoryBudgetSettingsModal'
import messages from './messages'

export interface ConnectedBudgetCategoryBudgetSettingsModalProps {
  open?: boolean
  onDismiss?(): void
}

export const ConnectedBudgetCategoryBudgetSettingsModal = ({
  open = false,
  onDismiss = () => undefined,
}: ConnectedBudgetCategoryBudgetSettingsModalProps) => {
  const intl = useProcurifyIntl()

  const [savingSettings, setSavingSettings] = useState<boolean>(false)

  const [haveSettingsChanged, setHaveSettingsChanged] =
    useState<boolean>(false)

  const [isBudgetOverageEnabled, setIsBudgetOverageEnabled] =
    useState<boolean>(false)

  useFetchIsBudgetOverageEnabled({
    onSuccess: ({
      data: { budgetOvertureToggle: budgetOverageResponse } = {
        budgetOvertureToggle: false,
      },
    }) => {
      setIsBudgetOverageEnabled(budgetOverageResponse)
    },
    onError: (error) => {
      toast.error({
        children: parseAPIError(error.data).message,
        title: intl.formatMessage(messages.errorNotificationTitle),
      })
    },
  })

  // with the addition of future options turn this into a generic function
  const onBudgetOverageChange = () => {
    setIsBudgetOverageEnabled(!isBudgetOverageEnabled)
    setHaveSettingsChanged(!haveSettingsChanged)
  }

  const saveBudgetSettingsChanges = () => {
    setSavingSettings(true)
    if (haveSettingsChanged) {
      toggleIsBudgetOverageEnabled({
        budgetOvertureToggle: isBudgetOverageEnabled,
      })
        .then(({ data: { budgetOvertureToggle } }) => {
          setSavingSettings(false)
          toast.success({
            children: intl.formatMessage(
              budgetOvertureToggle
                ? messages.budgetOverageEnabledNotificationText
                : messages.budgetOverageDisabledNotificationText
            ),
            title: intl.formatMessage(messages.successNotificationTitle),
          })
          onDismiss()
        })
        .catch(() => {
          setSavingSettings(false)
          // on failure reset
          setIsBudgetOverageEnabled(!isBudgetOverageEnabled)
          setHaveSettingsChanged(!haveSettingsChanged)
          toast.error({
            children: intl.formatMessage(messages.budgetOverageToggleErrorText),
            title: intl.formatMessage(messages.errorNotificationTitle),
          })
          onDismiss()
        })
    } else {
      setSavingSettings(false)
      onDismiss()
    }
  }

  return (
    <BudgetCategoryBudgetSettingsModal
      open={open}
      onDismiss={onDismiss}
      isBudgetOverageEnabled={isBudgetOverageEnabled}
      haveSettingsChanged={haveSettingsChanged}
      savingSettings={savingSettings}
      onBudgetOverageChange={onBudgetOverageChange}
      saveBudgetSettingsChanges={saveBudgetSettingsChanges}
    />
  )
}

ConnectedBudgetCategoryBudgetSettingsModal.displayName =
  'ConnectedBudgetCategoryBudgetSettingsModal'
